import React, { useCallback, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import {
  Banner,
  Button,
  FieldBox,
  NativeInput,
} from '@pushwoosh/dumb-components';
import { Color } from '@pushwoosh/kit-constants';
import { Horizontal } from '@pushwoosh/kit-helpers';
import { Text } from '@pushwoosh/kit-typography';

import { useCountdown } from '~/src/hooks/useCountdown';
import { useEnvironment } from '~/src/contexts/environment';
import { validateEmail } from '~/src/helpers/validation';
import { H1, RouterLink } from '~/src/ui-kit';
import { ContentBox } from '~/src/components/ContentBox';
import { ButtonLoader } from '~/src/components/ButtonLoader';
import { Lang, useLang } from '~/src/polyglot';

import {
  FormStyled,
  HeaderBox,
  MessageBox,
  InputFieldsBox,
} from './styles';
import type { ForgotPasswordFormValues, ForgotPasswordProps } from './ForgotPassword.types';

export function ForgotPassword({
  errorMessage,
  initialEmail,
  sendTokenHandler,
  resultMessage,
}: ForgotPasswordProps): ReactElement {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      resetEmail: initialEmail,
    },
  });

  const { waitSeconds, setCountStartDate } = useCountdown(60);
  const { getLinkByName, productName } = useEnvironment();
  const isAllowRegister = productName === 'Pushwoosh';
  const lang = useLang();

  const onSendClick = useCallback(
    (payload: ForgotPasswordFormValues) => sendTokenHandler(payload)
      .then(() => setCountStartDate(Date.now())),
    [sendTokenHandler, setCountStartDate],
  );

  return (
    <>
      <ContentBox>
        <FormStyled onSubmit={handleSubmit(onSendClick)}>
          <HeaderBox>
            <H1><Lang message="FORGOT_PASSWORD.TITLE" /></H1>
            {resultMessage && <MessageBox>{resultMessage}</MessageBox>}
          </HeaderBox>
          <InputFieldsBox>
            {!resultMessage && (
              <FieldBox
                title={lang({ message: 'FORGOT_PASSWORD.FIELD.EMAIL' })}
                error={!!errors.resetEmail && (
                  <Text $variant="footnote" $color={Color.DANGER}>
                    <Lang message="FORGOT_PASSWORD.ERROR.INVALID_EMAIL" />
                  </Text>
                )}
              >
                <NativeInput
                  autoFocus
                  $isErrored={!!errors.resetEmail}
                  {...register('resetEmail', { required: true, validate: validateEmail })}
                />
              </FieldBox>
            )}
            {!!errorMessage && !isSubmitting && <Banner type="error" title={errorMessage} />}
          </InputFieldsBox>
          {(resultMessage && waitSeconds)
            ? (
              <ButtonLoader>
                <Lang message="FORGOT_PASSWORD.BUTTON.RESEND_WAIT" seconds={waitSeconds} />
              </ButtonLoader>
            ) : (
              <Horizontal $alignItems="center">
                <Button
                  type="submit"
                  color="primary"
                  isLoading={isSubmitting}
                >
                  <Lang message="FORGOT_PASSWORD.BUTTON.SEND" />
                </Button>
              </Horizontal>
            )}
        </FormStyled>
      </ContentBox>
      <Horizontal $alignItems="center" $gap={4}>
        <RouterLink title={lang({ message: 'FORGOT_PASSWORD.LINK.SIGN_IN' })} to={getLinkByName('login')}>
          <Lang message="FORGOT_PASSWORD.LINK.SIGN_IN" />
        </RouterLink>
        {' '}
        <Lang message="FORGOT_PASSWORD.FOOTER.TO_ANOTHER_ACCOUNT" />
        {isAllowRegister && (
          <>
            {' '}
            <Lang message="FORGOT_PASSWORD.FOOTER.OR" />
            {' '}
            <RouterLink title={lang({ message: 'FORGOT_PASSWORD.LINK.SIGN_UP' })} to={getLinkByName('register')}>
              <Lang message="FORGOT_PASSWORD.LINK.SIGN_UP" />
            </RouterLink>
          </>
        )}
      </Horizontal>
    </>
  );
}
